<template>
    <!-- renderer SVG: el texto de la leyenda es DOM vectorial (no rasterizado al
        canvas), evita el recorte de píxeles del tope de las mayúsculas. -->
    <EChart v-if="chartReady" :option="chartOption" :init-options="{ renderer: 'svg' }" />
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import { interpolateHexPalette } from "@/utils/colorPalette";

// Anchors del gradiente teal del tab Ventas. Los top N clientes ocupan
// posiciones intermedias del gradient; "Otros" sale del gradient hacia su
// propio token (violeta) para que se lea como agregado distinto.
const LIGHTEST_ANCHOR = "--report-chart-segment-3";
const DARKEST_ANCHOR = "--report-chart-segment-1";
const OTROS_ANCHOR = "--report-chart-other";
const TEXT_MUTED_ANCHOR = "--report-text-muted";
const TEXT_ANCHOR = "--report-text";
const BORDER_ANCHOR = "--report-border";

const OTROS_NAME = "Otros";
// Razones sociales completas ("INDUSTRIAS Y COMERCIALIZADORA DE PRODUCTOS DE
// ACERO HHH SPA") empujan el legend a varias líneas y le roban verticalidad al
// chart. 20 caracteres preservan reconocimiento (suficiente para distinguir
// clientes habituales) sin reventar la barra del legend.
const LEGEND_NAME_MAX = 20;

export default {
    name: "SalesClientsStackedChart",

    components: { EChart },

    props: {
        // Headers ya formateados (ej. ["abr 2024", "may 2024", ...]). El store
        // los entrega listos para alinearlos con `series[i].values`.
        monthHeaders: { type: Array, required: true },
        // Array paralelo a monthHeaders. La última entrada con `name === 'Otros'`
        // (si existe) usa el color especial del token --report-chart-other; el
        // resto se reparte el gradiente teal.
        series: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((s) =>
                typeof s.name === "string" && Array.isArray(s.values)
            ),
        },
        // Toggle del popover del tooltip. Cuando false, el chart conserva el
        // axisPointer (shadow al hovear el mes) pero no levanta el card con
        // el breakdown — útil para lecturas rápidas donde el detalle estorba.
        // Implementado vía `tooltip.showContent`, no `tooltip.show`: si lo
        // apagáramos completo perderíamos también el shadow del hover.
        showTooltip: { type: Boolean, default: true },
    },

    data() {
        // chartReady arranca false porque el canvas de ECharts no resuelve
        // var(...). Resolvemos los anchors en mounted via getComputedStyle y
        // los inyectamos como hex en el option. Si algún anchor no se resuelve
        // (theme mal configurado o caímos al :root sin override), no rendereamos
        // el chart — mejor que pintar series sin color identificable.
        return {
            chartReady: false,
            lightAnchor: "",
            darkAnchor: "",
            otrosColor: "",
            textColor: "",
            textMutedColor: "",
            borderColor: "",
        };
    },

    computed: {
        // Series con "Otros" siempre al final (si existe), y el resto rankeado
        // como llega del store. Es un cheap pero importante guard: el caller
        // controla el orden de los top N (por monto descendente del backend);
        // acá sólo aseguramos que Otros no quede en medio si por algún cambio
        // futuro la API lo emitiera fuera de orden.
        orderedSeries() {
            const top = this.series.filter((s) => s.name !== OTROS_NAME);
            const otros = this.series.find((s) => s.name === OTROS_NAME);
            return otros ? [...top, otros] : top;
        },

        topCount() {
            return this.orderedSeries.filter((s) => s.name !== OTROS_NAME).length;
        },

        // Paleta hex por serie: N anchors del gradiente para los top + el
        // token de Otros como cierre. Si hay 1 sólo top, devolvemos el más
        // oscuro (no tiene sentido un gradiente de 1 entrada).
        palette() {
            if (!this.lightAnchor || !this.darkAnchor) return [];
            const topPalette = this.topCount > 1
                ? interpolateHexPalette(this.darkAnchor, this.lightAnchor, this.topCount)
                : (this.topCount === 1 ? [this.darkAnchor] : []);
            const hasOtros = this.orderedSeries.some((s) => s.name === OTROS_NAME);
            return hasOtros ? [...topPalette, this.otrosColor] : topPalette;
        },

        chartOption() {
            const palette = this.palette;
            return {
                grid: {
                    top: 48,
                    right: 16,
                    bottom: 32,
                    left: 56,
                    containLabel: false,
                },
                legend: {
                    show: true,
                    // Una sola fila con scroll (deslizante de paginado): los
                    // clientes que no entran se navegan con las flechas, sin
                    // robarle alto al gráfico (vs. envolver a 2 líneas).
                    type: "scroll",
                    left: 0,
                    // `width` (no `right`, que el modo scroll ignora) acota la
                    // leyenda para dejar a la derecha el espacio de la toolbar
                    // superpuesta (1 botón "Detalle"), en la misma fila, sin
                    // solaparse.
                    width: "80%",
                    top: 4,
                    itemGap: 16,
                    icon: "circle",
                    itemWidth: 8,
                    itemHeight: 8,
                    textStyle: {
                        color: this.textColor,
                        fontSize: 12,
                        fontWeight: 500,
                        // lineHeight ≥ fontSize: da alto de fila a la leyenda para
                        // que el clip vertical del modo scroll no recorte el tope
                        // del texto (itemHeight 8 < fontSize 12 lo dejaba corto).
                        lineHeight: 18,
                    },
                    // Trunca el texto visible a `LEGEND_NAME_MAX` caracteres
                    // + ellipsis. El nombre real de la serie no cambia: la
                    // serie sigue keyed por el nombre completo (tooltip,
                    // matching de clicks de legend, etc.), sólo se acorta el
                    // label que el legend pinta.
                    formatter: (name) => (
                        name.length > LEGEND_NAME_MAX
                            ? `${name.slice(0, LEGEND_NAME_MAX)}…`
                            : name
                    ),
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    // `showContent: false` apaga el popover del tooltip pero
                    // conserva el axisPointer (shadow sobre la barra hovereada).
                    // No usamos `show: false` porque eso también desactiva el
                    // axisPointer y perderíamos la afordancia de hover.
                    showContent: this.showTooltip,
                    // `confine: true` mantiene el tooltip dentro del chart
                    // canvas — sin esto, al hovear las primeras barras se
                    // desborda por la izquierda y los nombres largos de cliente
                    // (razones sociales completas) quedan cortados afuera del
                    // card. `extraCssText` impone un ancho máximo para que
                    // nombres muy largos hagan wrap en lugar de hacer crecer
                    // el tooltip a 600px+.
                    confine: true,
                    extraCssText: "max-width:280px; white-space:normal; word-break:break-word;",
                    backgroundColor: "#0f172a",
                    borderColor: "#0f172a",
                    textStyle: { color: "#f8fafc", fontSize: 12 },
                    // ECharts pasa un array (uno por serie). Formateamos las
                    // líneas + total para que el tooltip cuente lo mismo que
                    // ve el ojo en la barra.
                    formatter: (params) => {
                        if (!Array.isArray(params) || params.length === 0) return "";
                        const total = params.reduce(
                            (acc, p) => acc + (typeof p.value === "number" ? p.value : 0),
                            0,
                        );
                        // `min-width:0` en la celda del nombre + `flex-shrink:0`
                        // en la del monto: combinación necesaria para que los
                        // nombres largos hagan word-break sin empujar el monto
                        // fuera del tooltip ni romper el alineamiento derecho.
                        const rows = params
                            .filter((p) => typeof p.value === "number" && p.value !== 0)
                            .map((p) => `
                                <div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;">
                                    <span style="min-width:0;overflow-wrap:anywhere;">${p.marker}${p.seriesName}</span>
                                    <span style="flex-shrink:0;">${parseToMoney(p.value)}</span>
                                </div>
                            `)
                            .join("");
                        return `
                            <div style="font-weight:600;margin-bottom:6px;">${params[0].axisValueLabel}</div>
                            ${rows}
                            <div style="border-top:1px solid rgba(255,255,255,0.15);margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;gap:12px;">
                                <span>Total</span>
                                <span style="flex-shrink:0;"><strong>${parseToMoney(total)}</strong></span>
                            </div>
                        `;
                    },
                },
                xAxis: {
                    type: "category",
                    data: this.monthHeaders,
                    axisLine: { lineStyle: { color: this.borderColor } },
                    axisTick: { show: false },
                    axisLabel: {
                        color: this.textMutedColor,
                        fontSize: 11,
                        margin: 12,
                    },
                },
                yAxis: {
                    type: "value",
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: {
                        lineStyle: { color: this.borderColor, type: "dashed" },
                    },
                    axisLabel: {
                        color: this.textMutedColor,
                        fontSize: 11,
                        formatter: (val) => parseToCompactMoney(val),
                    },
                },
                series: this.orderedSeries.map((s, i) => ({
                    name: s.name,
                    type: "bar",
                    stack: "clients",
                    barMaxWidth: 36,
                    itemStyle: {
                        color: palette[i] || this.darkAnchor,
                        borderRadius: 0,
                    },
                    emphasis: {
                        focus: "series",
                        itemStyle: { opacity: 0.9 },
                    },
                    data: s.values,
                })),
            };
        },
    },

    mounted() {
        this.resolveCssColors();
        this.chartReady = Boolean(this.lightAnchor && this.darkAnchor && this.otrosColor);
    },

    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            const root = getComputedStyle(document.documentElement);
            this.lightAnchor = root.getPropertyValue(LIGHTEST_ANCHOR).trim();
            this.darkAnchor = root.getPropertyValue(DARKEST_ANCHOR).trim();
            this.otrosColor = root.getPropertyValue(OTROS_ANCHOR).trim();
            this.textColor = root.getPropertyValue(TEXT_ANCHOR).trim();
            this.textMutedColor = root.getPropertyValue(TEXT_MUTED_ANCHOR).trim();
            this.borderColor = root.getPropertyValue(BORDER_ANCHOR).trim();
        },
    },
};
</script>

<style scoped lang="scss">
// Wrapper transparente — el contenedor (`SalesChartsRow`) aplica la card
// (surface, borde, padding). Así el chart es reusable en otros layouts sin
// arrastrar decoración.
:deep(.echart) {
    width: 100%;
    height: 100%;
}
</style>
